import React, { useEffect } from 'react'
import { Badge, Dropdown, List, Button, Typography, Empty, Spin, Space } from 'antd'
import { BellOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { getUserNotifications, markNotificationAsRead, dismissNotification, bulkReadNotifications, bulkDismissNotifications } from './actions'
import { NotificationT } from '../../types/notificationT'

const notificationButtonStyles = `
  .notification-item-button {
    display: block !important;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    margin: 0;
    outline: none;
  }
  .notification-item-button:hover {
    background-color: #f5f5f5 !important;
  }
  .notification-item-button:focus {
    outline: 2px solid #1890ff;
    outline-offset: 2px;
  }
`

const { Text, Paragraph } = Typography

const styles = {
  notificationButton: {
    padding: '8px 0',
    maxWidth: 320,
    cursor: 'pointer',
    borderRadius: '4px',
    transition: 'background-color 0.2s',
    border: 'none',
    background: 'transparent',
    width: '100%',
    textAlign: 'left' as const
  },
  dropdownContainer: {
    minWidth: 350,
    maxWidth: 400,
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 0 10px #cccccc'
  },
  dropdownHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0'
  },
  scrollableContent: {
    maxHeight: 400,
    overflowY: 'auto' as const
  },
  loadingContainer: {
    padding: '20px',
    textAlign: 'center' as const
  },
  emptyContainer: {
    padding: '20px'
  },
  listItem: {
    padding: '8px 16px',
    borderBottom: '1px solid #f5f5f5'
  },
  viewAllContainer: {
    padding: '8px 16px',
    borderTop: '1px solid #f0f0f0',
    textAlign: 'center' as const
  },
  errorContainer: {
    padding: '8px 16px',
    borderTop: '1px solid #f0f0f0',
    backgroundColor: '#fff2f0'
  }
}

function NotificationDropdown() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    updatingNotifications,
    isBulkReading,
    isBulkDismissing
  } = useAppSelector((state) => state.userNotifications)

  useEffect(() => {
    dispatch(getUserNotifications({ limit: 20 }))
  }, [dispatch])

  const handleMarkAsRead = (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(markNotificationAsRead(notificationId))
  }

  const handleDismiss = (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(dismissNotification(notificationId))
  }

  const handleReadAll = () => {
  const unreadNotificationIds = notifications
    .filter(n => n.status === 'sent' || n.status === 'delivered')
    .map(n => n.id)
    dispatch(bulkReadNotifications(unreadNotificationIds))
  }

  const handleDismissAll = () => {
    const dismissableNotificationIds = notifications
      .filter(n => n.status === 'sent' || n.status === 'delivered' || n.status === 'read')
      .map(n => n.id)
    dispatch(bulkDismissNotifications(dismissableNotificationIds))
  }

  const handleNotificationClick = (notification: NotificationT) => {
    if (notification.status !== 'read') {
      dispatch(markNotificationAsRead(notification.id))
    }

    const incidentId = notification.data?.incident_id || notification.data?.incidentId
    navigate(`/incident/${incidentId}`)
  }

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInDays < 7) return `${diffInDays}d ago`
    return date.toLocaleDateString()
  }

  const visibleNotifications = notifications.filter(notification => notification.status !== 'dismissed')
  const dismissableNotifications = notifications.filter(n => n.status === 'sent' || n.status === 'delivered' || n.status === 'read')
  const notificationItems = visibleNotifications.slice(0, 10).map((notification) => ({
    key: notification.id,
    label: (
      <button
        type="button"
        style={styles.notificationButton}
        onClick={() => handleNotificationClick(notification)}
        className="notification-item-button"
        aria-label={`View incident details: ${notification.title}`}
      >
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text strong style={{ fontSize: '14px' }}>
              {notification.title}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {formatRelativeTime(notification.created)}
            </Text>
          </Space>

          <Paragraph
            ellipsis={{ rows: 2, tooltip: true }}
            style={{ margin: 0, fontSize: '13px', color: '#666' }}
          >
            {notification.message}
          </Paragraph>

          {notification.data && (
            <Space size={4}>
              {notification.data.offender && (
                <Text style={{ fontSize: '12px' }}>
                  Offender: {notification.data.offender}
                </Text>
              )}
              {notification.data.offender && notification.data.postcode && (
                <Text style={{ fontSize: '12px' }}>
                  -
                </Text>
              )}
              {notification.data.postcode && (
                <Text style={{ fontSize: '12px' }}>
                  Area: {notification.data.postcode}
                </Text>
              )}
            </Space>
          )}

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            {notification.status !== 'read' && notification.status !== 'dismissed' && (
              <Button
                type="text"
                size="small"
              icon={updatingNotifications.includes(notification.id) ? <Spin size="small" /> : <CheckOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                handleMarkAsRead(notification.id, e)
              }}
              disabled={updatingNotifications.includes(notification.id)}
                style={{ fontSize: '12px', padding: '0 4px', color: '#666' }}
              >
                Mark Read
              </Button>
            )}
            {notification.status !== 'dismissed' && (
              <Button
                type="text"
                size="small"
                danger
              icon={updatingNotifications.includes(notification.id) ? <Spin size="small" /> : <CloseOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                handleDismiss(notification.id, e)
              }}
              disabled={updatingNotifications.includes(notification.id)}
                style={{ fontSize: '12px', padding: '0 4px' }}
              >
                Dismiss
              </Button>
            )}
          </Space>
        </Space>
      </button>
    ),
  }))

  const dropdownContent = (
    <div style={styles.dropdownContainer}>
      <div style={styles.dropdownHeader}>
        <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>Notifications</Text>
          <Space size="small">
            {unreadCount > 0 && (
              <Button
                type="text"
                size="small"
                onClick={handleReadAll}
                loading={isBulkReading}
                disabled={isBulkReading || isBulkDismissing}
                style={{ fontSize: '12px', padding: '2px 8px' }}
              >
                Read All
              </Button>
            )}
            {dismissableNotifications.length > 0 && (
              <Button
                type="text"
                size="small"
                danger
                onClick={handleDismissAll}
                loading={isBulkDismissing}
                disabled={isBulkReading || isBulkDismissing}
                style={{ fontSize: '12px', padding: '2px 8px' }}
              >
                Dismiss All
              </Button>
            )}
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
            </Text>
          </Space>
        </Space>
      </div>

      <div style={styles.scrollableContent}>
        {isLoading && (
          <div style={styles.loadingContainer}>
            <Spin size="small" />
          </div>
        )}
        {!isLoading && visibleNotifications.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications yet"
            style={styles.emptyContainer}
          />
        )}
        {!isLoading && visibleNotifications.length > 0 && (
          <List
            dataSource={notificationItems}
            renderItem={(item) => (
              <List.Item style={styles.listItem}>
                {item.label}
              </List.Item>
            )}
          />
        )}
      </div>

      {visibleNotifications.length > 10 && (
        <div style={styles.viewAllContainer}>
          <Button type="link" size="small">
            View All Notifications
          </Button>
        </div>
      )}

      {error && (
        <div style={styles.errorContainer}>
          <Text type="danger" style={{ fontSize: '12px' }}>
            Failed to load notifications
          </Text>
        </div>
      )}
    </div>
  )

  return (
    <>
      <style>{notificationButtonStyles}</style>
      <Dropdown
        menu={{ items: [] }}
        dropdownRender={() => dropdownContent}
        trigger={['click']}
        placement="bottomRight"
        arrow
      >
        <Badge count={unreadCount} size="small" offset={[-2, 2]}>
          <Button
            type="text"
            icon={<BellOutlined style={{ fontSize: '18px' }} />}
            style={{
              border: 'none',
              boxShadow: 'none',
              padding: '8px',
              height: 'auto',
            }}
          />
        </Badge>
      </Dropdown>
    </>
  )
}

export default NotificationDropdown
